import objectIs from './objectIs';

export declare type EqualityFn = (
  newArgs: unknown,
  lastArgs: unknown
) => boolean;

/**
 * Returns a wrapper function that, when called, either:
 *
 * 1. Returns a cached result if given the same args as the last time it was called, or
 * 2. If any args are different from last time, calls through to `func`
 *
 * @example How to reset the internal cache
 *
 * ```typescript
 * import memoizeOne from 'react-utils/memoizeOne';
 *
 * const memoizeOneFunc = memoizeOne((num: number) => num);
 * memoizeOneFunc(1); // Call the function
 * memoizeOneFunc(1); // Use the cache
 * memoizeOneFunc.resetCache(); // Clear the internal cache
 * memoizeOneFunc(1); // Call the function
 * ```
 *
 * @param {Function} func
 * @param {?Function} equalityFn A function of the form `(a, b) => <bool>`
 * @returns {Function}
 */
export default function memoizeOne<
  ResultFn extends (this: any, ...newArgs: any[]) => ReturnType<ResultFn>,
>(
  func: ResultFn,
  equalityFn: EqualityFn = objectIs
): {
  (this: any, ...args: Parameters<ResultFn>): ReturnType<ResultFn>;
  /**
   * Reset the internal cache and return it's cache size before reset
   */
  resetCache(): number;
} {
  let hasBeenCalled = false;
  let lastArgs: Parameters<ResultFn>;
  let lastResult: ReturnType<ResultFn>;

  const memoizedFunc = function (this: any, ...args: Parameters<ResultFn>) {
    if (
      !hasBeenCalled ||
      args.length !== lastArgs.length ||
      args.some((arg, i) => !equalityFn(arg, lastArgs[i]))
    ) {
      hasBeenCalled = true;
      lastArgs = args;
      lastResult = func(...args);
    }

    return lastResult;
  };
  /**
   * Reset the internal cache and return it's cache size before reset
   */
  memoizedFunc.resetCache = () => {
    const cacheSize = hasBeenCalled ? 1 : 0;
    hasBeenCalled = false;
    return cacheSize;
  };
  return memoizedFunc;
}
